import React from "react";
import { shallowEqual } from "react-redux";
import { EcoTagType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { type UscDispatchType, useUscSelector } from "../../../../../redux/store";
import { showUscPriceSelector } from "../../../../../utils/priceUtils";
import UscFinanceToggle from "./UscFinanceToggle";
import UscMonthlyPriceDisclaimer from "../../price/UscMonthlyPriceDisclaimer";
import { enableUscMonthlyRate } from "../../../../../utils/settings";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_USC_BASKET_DETAILS } from "../../../../../utils/modalConstants";
import { UscLabel } from "../../../../../utils/labels";
import UscVehicleDisclaimers from "./UscVehicleDisclaimers";
import { getVehicleTitle, showFuelTypeTag } from "../../../../../../shared-logic/utils/uscUtils";
import * as Styles from "./UscVehicleInformation.styles";
import Icon from "../../../../../../shared-components/toyota/styles/atoms/Icon";
import Button from "../../../../../../shared-components/toyota/styles/atoms/Button";
import Text from "../../../../../../shared-components/toyota/styles/atoms/Text";
import InlineDisclaimer from "../../InlineDisclaimer";
import { UscDisclaimerType } from "../../../../../../shared-logic/types/UscCommonTypes";
import { DfVehicleStatus } from "../../../../../../shared-logic/utils/dfConstants";
import { type UscTrackingLocation, trackOpenBasketDetailsModal } from "../../../../../utils/tracking";
import UscInlinePrices from "../../price/UscInlinePrices";
import { showUscMonthlyDisclaimersSelector } from "../../../../../utils/disclaimerUtils";
import { useComponentRegistry } from "../../../componentRegistry";
import BasketVehicleCode from "../content/BasketVehicleCode";
import BasketVehicleSaleType from "../content/BasketVehicleSaleType";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

type UscVehicleInformationPropsType = {
    trackingLocation: UscTrackingLocation;
    inPdpContainer?: boolean;
};

const UscVehicleInformation = ({
    trackingLocation,
    inPdpContainer,
}: UscVehicleInformationPropsType): JSX.Element | null => {
    const dispatch = useDispatch<UscDispatchType>();
    const showPrice = useUscSelector(showUscPriceSelector);
    const result = useUscSelector((state) => state.vehicle.result);
    const showLocation = useUscSelector((state) => state.uscSettings.showLocation);
    const dealerCity = useUscSelector((state) => state.dealer?.dealer?.address.city);
    const enableMonthly = useUscSelector(({ commonSettings, uscSettings, vehicle }) =>
        enableUscMonthlyRate(commonSettings, uscSettings, vehicle.result, vehicle.type),
    );
    const showMonthlyDisclaimers = useUscSelector(showUscMonthlyDisclaimersSelector, shallowEqual);
    const activeFinanceOption = useUscSelector((state) => state.basket.activeFinanceOption);
    const uscContext = useUscSelector((state) => state.vehicle.type);
    const monthlyLoading = useUscSelector((state) => state.finance.fetching || state.externalPrice.fetching);

    const SaveButton = useComponentRegistry("SaveButton");
    const UscFinanceOptionsButton = useComponentRegistry("UscFinanceOptionsButton");

    if (!result) return null;

    const vehicleTitle = getVehicleTitle(result.product);
    const { engine, versionName } = result.product;
    const { code: fuelTypeCode, description: fuelTypeName } = engine.marketingFuelType;

    return (
        <Styles.Wrapper>
            <Styles.Header>
                <div>
                    <div>
                        <Text tag="h1" variant="h4" data-cpdqm={inPdpContainer ? "ignore" : undefined}>
                            {vehicleTitle}
                        </Text>
                        <Styles.VehicleInfo>
                            {showFuelTypeTag(fuelTypeCode) && (
                                <Text tag="span" variant={EcoTagType.Hybrid}>
                                    {fuelTypeName}
                                </Text>
                            )}
                            {!!versionName && (
                                <Text tag="span" variant="body-small-regular" color="grey-7">
                                    {versionName}
                                </Text>
                            )}
                        </Styles.VehicleInfo>
                    </div>
                    {showLocation && dealerCity && (
                        <Styles.VehicleDealerCity>
                            <Icon variant="location" />
                            <Text tag="p" variant="body-small-book">
                                {dealerCity}
                            </Text>
                        </Styles.VehicleDealerCity>
                    )}
                </div>
                <SaveButton trackingLocation={trackingLocation} />
            </Styles.Header>
            <BasketVehicleSaleType />
            {showPrice && (
                <>
                    <div>
                        <UscFinanceToggle trackingLocation={trackingLocation} />
                        <UscInlinePrices />
                        {showMonthlyDisclaimers && <UscMonthlyPriceDisclaimer isPlaceholder={monthlyLoading} />}

                        <InlineDisclaimer
                            disclaimerType={
                                activeFinanceOption === "cash" ? UscDisclaimerType.Cash : UscDisclaimerType.Finance
                            }
                        />
                    </div>
                    {result.vehicleStatus.code !== DfVehicleStatus.Sold && (
                        <Styles.ButtonCtaWrapper>
                            {enableMonthly && <UscFinanceOptionsButton trackingLocation={trackingLocation} />}
                            <Button
                                variant="basket-cta"
                                onClick={() => {
                                    trackOpenBasketDetailsModal(trackingLocation, uscContext);
                                    dispatch(showModal({ type: MODAL_USC_BASKET_DETAILS }));
                                }}
                            >
                                <Icon variant="basket-variant" />
                                <UscLabel label="uscBasketCtaBasketDetails" />
                            </Button>
                        </Styles.ButtonCtaWrapper>
                    )}
                    <Styles.VehicleCodeWrapper>
                        <BasketVehicleCode />
                    </Styles.VehicleCodeWrapper>
                </>
            )}
            <UscVehicleDisclaimers />
        </Styles.Wrapper>
    );
};

export default UscVehicleInformation;
